import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, ShoppingCart, X, AlertTriangle, Send, Lock, CheckCircle2, FileDown, TrendingUp, Sparkles, CalendarClock, Pencil } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import MultidocSummary from "@/components/MultidocSummary";
import PremiumFinalCheck from "@/components/PremiumFinalCheck";
import { getCase, getScore, api, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

function classificationColor(cls) {
  if (cls?.includes("adeguatamente")) return "text-forensic-green bg-forensic-green/10 border-forensic-green/30";
  if (cls?.includes("integrazioni")) return "text-forensic-amber bg-forensic-amber/10 border-forensic-amber/30";
  // FASE E.3 · TURNO 1 (B1) — both the internal ("debole") and the
  // presentation ("Preparazione documentale da migliorare") labels share
  // the same color bucket.
  if (cls?.includes("debole") || cls?.includes("migliorare")) return "text-forensic-red bg-forensic-red/10 border-forensic-red/30";
  return "text-slate-700 bg-slate-100 border-slate-300";
}

// FASE E.3 · TURNO 1 (B1) — internal → presentation label remap.
// Internal `score.classification` remains the source of truth for logic
// and analytics. `presentation_classification` is emitted by the backend
// for user-facing surfaces; this local helper is a safety fallback.
const _PRESENTATION_CLASSIFICATION = {
  "Fascicolo documentalmente debole": "Preparazione documentale da migliorare",
};
function presentationClassification(score) {
  return score?.presentation_classification
    || _PRESENTATION_CLASSIFICATION[score?.classification]
    || score?.classification
    || "";
}

// FASE E.3 · TURNO 1 (B2) — same disclaimer wording emitted by the PDF
// (scoring.PRESENTATION_SCORE_DISCLAIMER). Duplicated once on the client
// so the framing is visible even before the score payload arrives.
const PRESENTATION_SCORE_DISCLAIMER =
  "Questo risultato riguarda esclusivamente la preparazione documentale del fascicolo sulla base delle informazioni attualmente disponibili. Non esprime un giudizio sull'autenticità o falsità della scrittura, sulla fondatezza della contestazione o sull'esito di un eventuale accertamento.";

export default function CaseDashboard() {
  const { id } = useParams();
  const nav = useNavigate();
  const { isAdmin } = useAuth();
  const [caseData, setCaseData] = useState(null);
  const [score, setScore] = useState(null);
  const [summary, setSummary] = useState(null);
  // FASE E.3 · TURNO 2C.5 — read the deterministic `effective_user_type`
  // computed backend-side (legacy-compatible resolver). Frontend gating
  // MUST NOT rely on the raw `user_type` field, which can be missing on
  // legacy Cases and would fall back to "private" incorrectly.
  const userType = caseData?.effective_user_type || caseData?.user_type || "private";
  const doc = caseData;
  const sendToConsultant = () => setShowSend(true);
  const [showSend, setShowSend] = useState(false);
  const [showCheckout, setShowCheckout] = useState(false);
  const [sendForm, setSendForm] = useState({ name: "", email: "", phone: "", role: "privato", message: "", privacy_read: false, art9_consent: false });
  const [sending, setSending] = useState(false);
  const [checkoutForm, setCheckoutForm] = useState({
    first_name: "", last_name: "", fiscal_code: "", vat_number: "",
    address_street: "", address_zip: "", address_city: "", address_province: "",
    email: "", pec: "", sdi_code: "", phone: "",
  });
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [consistency, setConsistency] = useState(null);
  const canBuyPremium = !consistency || consistency.consistency_status !== "inconsistent";

  useEffect(() => {
    (async () => {
      try {
        const [c, s, sm] = await Promise.all([
          getCase(id), getScore(id),
          api.get(`/cases/${id}/summary`).then((r) => r.data),
        ]);
        setCaseData(c); setScore(s); setSummary(sm);
      } catch { toast.error("Impossibile caricare il fascicolo"); }
    })();
  }, [id]);

  const submitFreeSend = async () => {
    if (!sendForm.name || !sendForm.email) { toast.error("Nome ed email obbligatori"); return; }
    if (!sendForm.privacy_read) { toast.error("Conferma la lettura dell'informativa privacy"); return; }
    // If clinical data present in case, require art9 too
    const clinical = caseData?.clinical || {};
    const hasClinical = (clinical.documents?.length || clinical.conditions?.length || clinical.ricoveri?.length);
    if (hasClinical && !sendForm.art9_consent) {
      toast.error("Sono stati inseriti dati sanitari: consenso art. 9 GDPR obbligatorio");
      return;
    }
    setSending(true);
    try {
      const r = await api.post(`/cases/${id}/send-free`, { ...sendForm, case_id: id });
      if (r?.data?.already_sent) {
        toast(r.data.message || "Il report è già stato inviato al consulente.");
      } else {
        toast.success("Report inviato al consulente");
      }
      setShowSend(false);
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore invio"); }
    finally { setSending(false); }
  };

  const submitCheckout = async () => {
    const required = ["first_name", "last_name", "address_street", "address_zip", "address_city", "address_province", "email"];
    for (const k of required) {
      if (!checkoutForm[k]?.trim()) { toast.error("Compila tutti i campi obbligatori (*)"); return; }
    }
    if (!checkoutForm.fiscal_code && !checkoutForm.vat_number) {
      toast.error("Inserisci Codice Fiscale o Partita IVA"); return;
    }
    setCheckoutLoading(true);
    try {
      // FASE C.2 — server-side gate: refuse checkout if blocking issues remain.
      const pre = await api.get(`/cases/${id}/premium/precheck`);
      setConsistency(pre.data);
      if (pre.data?.consistency_status === "inconsistent") {
        toast.error("Il report non può essere generato finché sono presenti incoerenze bloccanti.");
        setCheckoutLoading(false);
        setShowCheckout(false);
        return;
      }
      const r = await api.post(`/cases/${id}/fascicolo-checkout`, {
        case_id: id, ...checkoutForm, origin_url: window.location.origin,
      });
      window.location.href = r.data.checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore checkout");
      setCheckoutLoading(false);
    }
  };

  if (!caseData || !score || !summary) {
    return (
      <div className="min-h-screen bg-paper flex flex-col">
        <Header />
        <main className="flex-1 flex items-center justify-center">
          <Loader2 className="animate-spin text-navy" />
        </main>
        <Footer />
      </div>
    );
  }

  const firstRecommendation = score.recommendations?.[0];
  const restCount = Math.max(0, (score.recommendations?.length || 0) - 1);
  const urgency = summary.urgency;
  const workability = summary.workability;
  const nextSvc = summary.next_service;
  const firstAction = summary.first_action;

  const badgeClass = (level) => {
    const map = {
      "Normale": "border-forensic-green text-forensic-green bg-forensic-green/10",
      "Moderata": "border-forensic-amber text-forensic-amber bg-forensic-amber/10",
      "Alta": "border-orange-600 text-orange-700 bg-orange-100",
      "Critica": "border-forensic-red text-forensic-red bg-forensic-red/10",
      "Ottima": "border-forensic-green text-forensic-green bg-forensic-green/10",
      "Buona": "border-forensic-green text-forensic-green bg-forensic-green/10",
      "Media": "border-forensic-amber text-forensic-amber bg-forensic-amber/10",
      "Ridotta": "border-orange-600 text-orange-700 bg-orange-100",
      "Molto ridotta": "border-forensic-red text-forensic-red bg-forensic-red/10",
      "Limitata": "border-orange-600 text-orange-700 bg-orange-100",
      "Molto limitata": "border-forensic-red text-forensic-red bg-forensic-red/10",
    };
    return map[level] || "border-slate-300 text-slate-600 bg-slate-100";
  };

  return (
    <div className="min-h-screen bg-paper flex flex-col">
      <Header />
      <main className="flex-1 max-w-6xl mx-auto px-6 py-10 w-full">
        <div>
          <div className="overline mb-1">Fascicolo</div>
          <div className="font-mono text-xs text-slate-500" data-testid="case-id">#{id}</div>
          <h1 className="font-serif text-3xl lg:text-5xl font-semibold tracking-tight mt-2">
            Report sommario preliminare
          </h1>
          <p className="text-sm text-slate-600 mt-3 max-w-2xl leading-relaxed">
            Questo riepilogo valuta la <strong>completezza documentale</strong> del tuo fascicolo.
            Non esprime alcun giudizio sull'autenticità della scrittura.
          </p>
        </div>

        {/* MULTIDOC OVERVIEW — B.3 */}
        {summary?.questionedDocumentAssessment?.length >= 2 && (
          <section className="mt-8" data-testid="multidoc-dashboard-section">
            <MultidocSummary assessments={summary.questionedDocumentAssessment} defaultExpanded={false} />
          </section>
        )}

        {/* SCORE + PROFILE — legacy single-doc only */}
        {!(summary?.questionedDocumentAssessment?.length >= 2) && (
        <section className="mt-8 grid lg:grid-cols-3 gap-4">
          <div className="border-2 border-navy bg-white p-8 lg:col-span-1">
            <div className="overline">Indice di preparazione documentale</div>
            <div className="mt-4 font-serif text-7xl font-semibold leading-none text-navy" data-testid="score-total">
              {score.total}<span className="text-3xl text-slate-400">/100</span>
            </div>
            <div className={`inline-block mt-6 px-3 py-1 rounded-sm text-xs uppercase tracking-wider border ${classificationColor(score.classification)}`}
              data-testid="score-classification">
              {presentationClassification(score)}
            </div>
            <p className="mt-4 text-xs text-slate-600 leading-relaxed italic" data-testid="score-framing-disclaimer">
              {PRESENTATION_SCORE_DISCLAIMER}
            </p>
            <div className="mt-5 grid grid-cols-2 gap-3">
              <div title={urgency?.reasons?.join("\n")} data-testid="urgency-badge"
                className={`border p-3 rounded-sm ${badgeClass(urgency?.level)}`}>
                <div className="text-[10px] uppercase tracking-widest font-mono opacity-80">Urgenza</div>
                <div className="font-serif text-lg font-semibold mt-1">{urgency?.level}</div>
              </div>
              <div title={workability?.reasons?.join("\n")} data-testid="workability-badge"
                className={`border p-3 rounded-sm ${badgeClass(workability?.level)}`}>
                <div className="text-[10px] uppercase tracking-widest font-mono opacity-80">Lavorabilità</div>
                <div className="font-serif text-lg font-semibold mt-1">{workability?.level}</div>
              </div>
            </div>
          </div>
          <div className="border hairline bg-white p-6 lg:col-span-2">
            <div className="overline mb-4">Dettaglio per area</div>
            <div className="space-y-3">
              {score.areas.map((a) => {
                const pct = (a.score / a.max) * 100;
                return (
                  <div key={a.key} data-testid={`area-${a.key}`}>
                    <div className="flex justify-between text-sm">
                      <span>{a.name}</span>
                      <span className="font-mono text-slate-600">{a.score}/{a.max}</span>
                    </div>
                    <div className="h-1.5 bg-slate-200 mt-1 relative">
                      <div className="absolute inset-y-0 left-0 bg-navy" style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
            {nextSvc && (
              <div className="mt-6 border-l-2 border-navy bg-navy/5 p-4" data-testid="next-service-block">
                <div className="overline mb-1">Servizio potenzialmente pertinente</div>
                <div className="font-serif text-lg font-semibold">{nextSvc.title}</div>
                <p className="text-sm text-slate-600 mt-1 leading-snug">{nextSvc.detail}</p>
                <p className="text-[11px] italic text-slate-500 mt-2">{nextSvc.note}</p>
              </div>
            )}
          </div>
        </section>
        )}

        {/* SUMMARY: PROFILE + WARNINGS + FIRST RECO */}
        <section className="mt-6 border hairline bg-white p-6 lg:p-8" data-testid="summary-section">
          <div className="overline mb-2">Profilo del caso</div>
          <h2 className="font-serif text-2xl lg:text-3xl font-semibold tracking-tight">
            {summary.profile_label}
          </h2>

          {summary.warnings?.length > 0 && (
            <div className="mt-6" data-testid="warnings-block">
              <div className="overline mb-3 text-forensic-amber flex items-center gap-1">
                <AlertTriangle size={12} /> Criticità rilevate
              </div>
              <ul className="space-y-2 text-sm text-slate-700">
                {summary.warnings.map((w, i) => (
                  <li key={i} className="pl-4 border-l-2 border-forensic-amber leading-relaxed">{w}</li>
                ))}
              </ul>
            </div>
          )}

          {firstRecommendation && (
            <div className="mt-6" data-testid="first-reco-block">
              <div className="overline mb-3 text-navy">Prima raccomandazione</div>
              <p className="pl-4 border-l-2 border-navy text-sm text-slate-700 leading-relaxed">
                {firstRecommendation}
              </p>
              {restCount > 0 && (
                <p className="mt-3 text-xs text-slate-500 flex items-center gap-1">
                  <Lock size={11} /> Altre <strong>{restCount}</strong> raccomandazioni + piano completo delle comparative disponibili nel fascicolo completo.
                </p>
              )}
            </div>
          )}

          {firstAction && (
            <div className="mt-6 border-l-2 border-forensic-green bg-forensic-green/5 p-4" data-testid="improvement-block">
              <div className="overline text-forensic-green mb-1">Come migliorare il fascicolo</div>
              {/* FASE E.3 · TURNO 1b Fix 2 — show up to 3 actions (source: build_improvement_actions). */}
              {(() => {
                const topActions = Array.isArray(summary.top_actions) && summary.top_actions.length > 0
                  ? summary.top_actions.slice(0, 3)
                  : [firstAction];
                return topActions.map((act, i) => (
                  <div key={i} className={i > 0 ? "mt-3 pt-3 border-t border-forensic-green/20" : ""}
                       data-testid={`improvement-action-${i}`}>
                    <div className="font-semibold text-slate-800">{act.title}</div>
                    {act.detail && (
                      <p className="text-sm text-slate-600 mt-1 leading-snug">{act.detail}</p>
                    )}
                    {act.impact && (
                      <p className="text-xs font-mono text-forensic-green mt-2">Impatto potenziale: {act.impact}</p>
                    )}
                    {!act.impact && act.estimated_delta && (
                      <p className="text-xs font-mono text-forensic-green mt-2">{act.estimated_delta}</p>
                    )}
                  </div>
                ));
              })()}
              {(summary.actions_count || 0) > 3 && (
                <p className="mt-3 text-xs text-slate-500 flex items-center gap-1">
                  <Lock size={11} /> Altre <strong>{summary.actions_count - 3}</strong> azioni prioritarie disponibili nel fascicolo completo.
                </p>
              )}
            </div>
          )}

          <p className="mt-6 text-xs text-slate-500 italic">{summary.closing_note}</p>
        </section>

        {/* COSA EMERGE SUBITO — highlights (top-3 strengths + top-3 crit) */}
        {summary.highlights && (
          <section className="mt-6 border-2 border-navy/30 bg-white p-6 lg:p-8" data-testid="highlights-section">
            <div className="overline mb-2 flex items-center gap-2 text-navy">
              <Sparkles size={13} /> Cosa emerge subito dal tuo fascicolo
            </div>
            <h2 className="font-serif text-2xl lg:text-3xl font-semibold tracking-tight">
              Un colpo d'occhio sui punti chiave.
            </h2>

            <div className="mt-6 grid md:grid-cols-2 gap-5">
              {summary.highlights.strengths?.length > 0 && (
                <div className="border-l-2 border-forensic-green bg-forensic-green/5 p-4">
                  <div className="overline text-forensic-green mb-2 flex items-center gap-1">
                    <CheckCircle2 size={12} /> Punti di forza
                  </div>
                  <ul className="space-y-2">
                    {summary.highlights.strengths.map((s, i) => (
                      <li key={i} className="flex gap-2 text-sm text-slate-700 leading-snug">
                        <span className="text-forensic-green font-bold shrink-0">✔</span>
                        <span>{s}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {summary.highlights.criticities?.length > 0 && (
                <div className="border-l-2 border-forensic-amber bg-forensic-amber/5 p-4">
                  <div className="overline text-forensic-amber mb-2 flex items-center gap-1">
                    <AlertTriangle size={12} /> Principali criticità
                  </div>
                  <ul className="space-y-2">
                    {summary.highlights.criticities.map((c, i) => (
                      <li key={i} className="flex gap-2 text-sm text-slate-700 leading-snug">
                        <span className="text-forensic-amber font-bold shrink-0">⚠</span>
                        <span>{c}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            {summary.highlights.why_it_matters && (
              <div className="mt-5 border-l-2 border-slate-300 bg-slate-50 p-4">
                <div className="overline mb-1 text-slate-700">Perché è importante</div>
                <p className="text-sm text-slate-700 leading-relaxed">{summary.highlights.why_it_matters}</p>
              </div>
            )}
          </section>
        )}

        {/* TIMELINE ALERTS — eventi cronologicamente prossimi al documento */}
        {summary.timeline_alerts && (summary.timeline_alerts.has_alerts || summary.timeline_alerts.annotated_timeline?.length > 0) && (
          <section className="mt-6 border hairline bg-white p-6" data-testid="timeline-alerts-section">
            <div className="overline mb-2 flex items-center gap-2 text-navy">
              <CalendarClock size={13} /> Timeline intelligente
            </div>
            <h3 className="font-serif text-xl font-semibold tracking-tight">
              Eventi da approfondire nella cronologia dichiarata
            </h3>
            {summary.timeline_alerts.has_alerts ? (
              <>
                <p className="text-sm text-slate-700 mt-3 leading-relaxed">
                  {summary.timeline_alerts.note}
                </p>
                <ul className="mt-4 space-y-2" data-testid="timeline-alerts-list">
                  {summary.timeline_alerts.alerts.map((a, i) => (
                    <li key={i} className="flex items-start gap-3 border-l-2 border-forensic-amber bg-forensic-amber/5 p-3">
                      <span className="text-forensic-amber text-lg shrink-0 leading-none">⚠</span>
                      <div className="min-w-0">
                        <div className="text-sm font-medium text-slate-800">
                          {a.label || "Evento dichiarato"}
                          <span className="text-forensic-amber text-xs font-mono ml-2">Approfondire</span>
                        </div>
                        <div className="text-[11px] text-slate-500 font-mono mt-0.5">
                          {(a.date || "").slice(0, 10)}
                          {typeof a.days_from_document === "number" && (
                            <span> · {a.days_from_document >= 0 ? "+" : ""}{a.days_from_document} giorni dal documento</span>
                          )}
                          {a.reason && <span> · {a.reason}</span>}
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
                <p className="text-[11px] italic text-slate-500 mt-3 leading-snug">
                  L'alert segnala solo una vicinanza temporale e non implica alcuna incidenza sul gesto grafico.
                  Il sistema non esprime giudizi tecnici.
                </p>
              </>
            ) : (
              <p className="text-sm text-slate-600 mt-3 leading-relaxed">
                {summary.timeline_alerts.note}
              </p>
            )}
          </section>
        )}

        {/* CTA teaser: cosa aggiunge il Report Premium */}
        <section className="mt-6 border-2 border-navy bg-navy text-white p-6 lg:p-8" data-testid="premium-teaser">
          <div className="overline text-forensic-amber mb-2 flex items-center gap-2">
            <TrendingUp size={13} /> Con il Report Premium potrai approfondire
          </div>
          <h3 className="font-serif text-2xl lg:text-3xl font-semibold tracking-tight">
            Non analizzi soltanto le scritture. Analizzi il loro contesto temporale.
          </h3>
          <p className="text-sm text-white/85 mt-3 leading-relaxed max-w-3xl">
            Il Report Premium trasforma un insieme di documenti in una visione organica del caso.
            Organizza cronologicamente le comparative, le collega agli eventi clinici e processuali,
            individua automaticamente possibili criticità documentali e fornisce una strategia concreta
            per impostare il lavoro del consulente e del difensore.
          </p>
          <ul className="mt-5 grid md:grid-cols-2 gap-x-6 gap-y-2 text-sm">
            {[
              "Analisi completa delle comparative.",
              "Timeline integrata con comparative, cartella clinica ed eventi.",
              "Individuazione automatica degli eventi che meritano approfondimento.",
              "Strategia documentale personalizzata.",
              "Piano delle comparative da reperire.",
              "Motivazione tecnica completa del punteggio.",
              "Indicazioni operative per il consulente e il difensore.",
            ].map((t) => (
              <li key={t} className="flex gap-2 text-white/90 leading-snug">
                <CheckCircle2 size={14} className="text-forensic-amber shrink-0 mt-1" />
                <span>{t}</span>
              </li>
            ))}
          </ul>
        </section>

        {/* COHERENCE WARNINGS */}
        {summary.coherence_warnings?.length > 0 && (
          <section className="mt-6 border-2 border-forensic-red bg-forensic-red/5 p-5" data-testid="coherence-warnings">
            <div className="overline mb-2 text-forensic-red flex items-center gap-1">
              <AlertTriangle size={12} /> Controlli di coerenza — {summary.coherence_warnings.length} segnalazioni
            </div>
            <p className="text-xs text-slate-600 italic mb-3">
              Le segnalazioni non modificano il punteggio: sono possibili incongruenze da verificare.
            </p>
            <ul className="space-y-2 text-sm">
              {summary.coherence_warnings.map((w, i) => (
                <li key={i} className={`pl-3 border-l-2 leading-snug ${w.severity === "high" ? "border-forensic-red text-slate-800" : w.severity === "medium" ? "border-forensic-amber text-slate-700" : "border-slate-400 text-slate-600"}`}>
                  <span className="text-[10px] font-mono uppercase mr-2">{w.severity}</span>
                  {w.message}
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* CLINICAL SECTION */}
        {summary.clinical?.level && summary.clinical.level !== "non documentata" && (
          <section className="mt-6 border hairline bg-white p-5" data-testid="clinical-panel">
            <div className="overline mb-2">Documentazione sanitaria</div>
            <p className="text-sm">Valutazione della disponibilità: <b>{summary.clinical.level}</b></p>
            <ul className="mt-2 text-xs text-slate-600 space-y-1">
              {summary.clinical.reasons.map((r, i) => <li key={i}>· {r}</li>)}
            </ul>
            <p className="text-[11px] italic text-slate-500 mt-3">{summary.clinical.note}</p>
          </section>
        )}

        {/* FASE C.2 — Controllo finale del fascicolo (mostrato prima delle CTA) */}
        <PremiumFinalCheck caseId={id} onStatusChange={setConsistency} />

        {/* CTA */}
        {/* FASE E.3 · TURNO 2B — Legacy Stripe €29,90 + "Parla con il consulente"
            gated: hidden for the segmented user_types (private/lawyer/professional).
            The floating CTA already provides the correct primary action per
            user_type (T2A). Kept renderable only when `user_type` is missing
            (backward compatibility with legacy cases created before T2A). */}
        {!["private", "lawyer", "professional"].includes((userType || "").toLowerCase()) && (
        <>
        <section className="mt-6 grid md:grid-cols-2 gap-4" data-testid="cta-section">
          <button onClick={() => setShowSend(true)} data-testid="send-free-btn"
            className="group border-2 hairline bg-white hover:border-forensic-green text-left p-6 transition-all">
            <div className="overline text-forensic-green mb-1">Gratis</div>
            <h3 className="font-serif text-2xl font-semibold">Parla con il consulente</h3>
            <p className="text-sm text-slate-600 mt-2 leading-relaxed">
              Invia il fascicolo al perito e ricevi un primo parere orale gratuito. Nessun impegno.
            </p>
            <span className="inline-flex items-center gap-2 mt-4 text-forensic-green text-sm font-medium group-hover:gap-3 transition-all">
              <Send size={14} /> Invia richiesta
            </span>
          </button>

          <button
            onClick={() => canBuyPremium && setShowCheckout(true)}
            disabled={!canBuyPremium}
            data-testid="buy-fascicolo-btn"
            data-can-generate={String(canBuyPremium)}
            className={`group border-2 border-navy text-left p-6 transition-all ${
              canBuyPremium
                ? "bg-navy text-white hover:bg-navy-dark"
                : "bg-slate-100 text-slate-400 border-slate-300 cursor-not-allowed"
            }`}>
            <div className={`overline mb-1 ${canBuyPremium ? "text-white/80" : "text-slate-500"}`}>
              {canBuyPremium ? "Genera report Premium" : "Report bloccato"}
            </div>
            <h3 className="font-serif text-2xl font-semibold">
              {canBuyPremium ? "Scarica il PDF completo · € 29,90" : "Correggi le incoerenze per procedere"}
            </h3>
            <p className={`text-sm mt-2 leading-relaxed ${canBuyPremium ? "text-white/80" : "text-slate-500"}`}>
              {canBuyPremium
                ? "PDF ordinato con indice, tutte le raccomandazioni, piano delle comparative, timeline e criticità procedurali."
                : "Il report non può essere generato finché sono presenti incoerenze bloccanti."}
            </p>
            {canBuyPremium && (
              <div className="mt-4 flex items-center gap-3 flex-wrap">
                <span className="inline-flex items-center gap-2 text-sm font-medium group-hover:gap-3 transition-all">
                  <ShoppingCart size={14} /> Procedi con Stripe
                </span>
                <span className="text-[11px] text-white/70">Regime forfettario · no IVA</span>
              </div>
            )}
          </button>
        </section>

        <p className="mt-4 text-xs text-slate-600 leading-relaxed max-w-3xl">
          <strong>Nota:</strong> in caso di conferimento incarico (parere, perizia, consulenza tecnica),
          il costo di € 29,90 sarà detratto dall'importo finale della prestazione.
        </p>
        </>
        )}

        {/* FASE E.3 · TURNO 2B — Private evidenzia GRATIS. */}
        {(userType || "").toLowerCase() === "private" && (
          <section className="mt-6 border-2 border-forensic-green bg-forensic-green/5 p-5 lg:p-6"
                   data-testid="private-value-badge">
            <div className="inline-block bg-forensic-green text-white text-xs font-bold tracking-wider px-2 py-1 rounded-sm mb-2">
              GRATIS · NESSUN PAGAMENTO
            </div>
            <h3 className="font-serif text-xl font-semibold text-ink">Risultato preliminare gratuito</h3>
            <p className="text-sm text-slate-700 mt-1 leading-relaxed max-w-2xl">
              Hai completato gratuitamente la valutazione preliminare del tuo fascicolo.
              Puoi inviare gratuitamente il fascicolo al consulente per richiedere una prima
              valutazione professionale, senza alcun impegno.
            </p>
            <button
              onClick={() => setShowSend(true)}
              data-testid="private-send-cta"
              className="mt-4 inline-flex items-center gap-2 bg-navy text-white hover:bg-navy-dark px-5 py-3 rounded-sm text-sm font-semibold">
              <Send size={14} /> Invia il fascicolo al consulente →
            </button>
          </section>
        )}

        {/* FASE E.3 · TURNO 1 (A) — Edit-fascicolo CTA (post-report). */}
        <section className="mt-6 border hairline bg-white p-5 lg:p-6 flex flex-wrap items-center justify-between gap-4"
          data-testid="edit-fascicolo-section">
          <div className="max-w-xl">
            <div className="overline mb-1 text-navy flex items-center gap-1">
              <Pencil size={12} /> Correzione dati fascicolo
            </div>
            <h3 className="font-serif text-xl font-semibold text-ink">Modifica i dati del fascicolo</h3>
            <p className="text-sm text-slate-600 mt-1 leading-relaxed">
              Hai notato un errore? Correggi i dati e genera una versione aggiornata del report.
              La versione precedente resta invariata come storico.
            </p>
          </div>
          <button
            onClick={() => nav(`/wizard?case_id=${id}`)}
            className="inline-flex items-center gap-2 border-2 border-navy text-navy hover:bg-navy hover:text-white px-4 py-2 rounded-sm text-sm font-medium transition-colors"
            data-testid="edit-fascicolo-btn">
            <Pencil size={14} /> Modifica i dati del fascicolo
          </button>
        </section>

        {/* ADMIN-ONLY DETAILS (visible only to logged-in admin) */}
        {isAdmin && (
          <section className="mt-10 border-2 border-navy/40 bg-navy/5 p-6" data-testid="admin-inline-panel">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div className="overline mb-3 text-navy flex items-center gap-2">
                <CheckCircle2 size={12} /> Vista amministratore — download gratuito
              </div>
              <div className="flex gap-2">
                <button
                  onClick={async () => {
                    try {
                      const res = await fetch(`${API}/admin/cases/${id}/fascicolo-pdf?audience=avvocato`, { credentials: "include" });
                      if (!res.ok) { toast.error("Errore download"); return; }
                      const blob = await res.blob();
                      const url = URL.createObjectURL(blob);
                      const a = document.createElement("a");
                      a.href = url; a.download = `fascicolo-avvocato-${id.slice(0, 8)}.pdf`; a.click();
                      URL.revokeObjectURL(url);
                      toast.success("PDF scaricato");
                    } catch { toast.error("Errore"); }
                  }}
                  className="inline-flex items-center gap-2 bg-navy hover:bg-navy-dark text-white px-3 py-2 rounded-sm text-xs"
                  data-testid="admin-dl-avvocato">
                  <FileDown size={12} /> PDF (avvocato)
                </button>
                <button
                  onClick={async () => {
                    try {
                      const res = await fetch(`${API}/admin/cases/${id}/fascicolo-pdf?audience=privato`, { credentials: "include" });
                      if (!res.ok) { toast.error("Errore download"); return; }
                      const blob = await res.blob();
                      const url = URL.createObjectURL(blob);
                      const a = document.createElement("a");
                      a.href = url; a.download = `fascicolo-privato-${id.slice(0, 8)}.pdf`; a.click();
                      URL.revokeObjectURL(url);
                      toast.success("PDF scaricato");
                    } catch { toast.error("Errore"); }
                  }}
                  className="inline-flex items-center gap-2 border border-navy text-navy hover:bg-navy hover:text-white px-3 py-2 rounded-sm text-xs"
                  data-testid="admin-dl-privato">
                  <FileDown size={12} /> PDF (privato)
                </button>
              </div>
            </div>
            <div className="grid md:grid-cols-2 gap-6 text-sm">
              <div>
                <div className="font-semibold mb-2">Identificazione</div>
                <ul className="space-y-1 text-slate-700">
                  <li>Tipo doc: {caseData.identification?.document_type || "—"}</li>
                  <li>Contestazione: {caseData.identification?.contestation_type || "—"}</li>
                  <li>Disponibilità: {caseData.identification?.document_availability || "—"}</li>
                  <li>Fase: {caseData.identification?.phase || "—"}</li>
                  <li>Custode originale: {caseData.identification?.original_holder || "—"}</li>
                </ul>
              </div>
              <div>
                <div className="font-semibold mb-2">Strategia legale</div>
                <ul className="space-y-1 text-slate-700">
                  <li>Firma: {caseData.legal_strategy?.signature_status || "—"}</li>
                  <li>Verificazione: {caseData.legal_strategy?.verificazione_requested ? "Sì" : "No"}</li>
                  <li>Querela di falso: {caseData.legal_strategy?.querela_falso ? "Sì" : "No"}</li>
                  <li>CTU: {caseData.legal_strategy?.ctu_disposed ? "Sì" : "No"}</li>
                </ul>
              </div>
              <div className="md:col-span-2">
                <div className="font-semibold mb-2">Richiedente</div>
                <div className="text-slate-700">
                  {[caseData.requester?.title, caseData.requester?.first_name, caseData.requester?.last_name].filter(Boolean).join(" ") || caseData.contact_name || "—"}
                  {caseData.requester?.organization && <> · {caseData.requester.organization}</>}
                  <> · {caseData.requester?.email || caseData.contact_email || "—"}</>
                  {(caseData.requester?.phone || caseData.contact_phone) && <> · {caseData.requester?.phone || caseData.contact_phone}</>}
                </div>
                {caseData.case_short_description && (
                  <div className="mt-3 border-l-2 border-navy bg-navy/5 px-3 py-2 text-xs text-slate-700 leading-snug" data-testid="dashboard-case-description">
                    <div className="overline mb-1">Breve descrizione del caso</div>
                    <p className="whitespace-pre-wrap">{caseData.case_short_description}</p>
                  </div>
                )}
              </div>
            </div>
          </section>
        )}

        {/* FASE E.3 · TURNO 2A — Floating CTA differentiated by user_type. */}
        {(() => {
          const ut = (userType || "private").toLowerCase();
          const alreadySent = !!doc?.sent_to_consultant;
          if (ut === "private") {
            if (alreadySent) {
              return (
                <div className="fixed bottom-4 right-4 lg:bottom-6 lg:right-6 z-40 bg-slate-800 text-white px-4 py-3 rounded-sm shadow-lg text-sm max-w-xs"
                     data-testid="floating-cta-private-sent">
                  <CheckCircle2 size={14} className="inline mr-1" /> Fascicolo inviato
                </div>
              );
            }
            return (
              <button
                onClick={() => {
                  if (!window.confirm("Inviare il fascicolo al consulente?\n\nIl consulente riceverà le informazioni inserite nel fascicolo per poter valutare la richiesta e ricontattarti.")) return;
                  sendToConsultant?.();
                }}
                className="fixed bottom-4 right-4 lg:bottom-6 lg:right-6 z-40 bg-navy text-white px-5 py-3 rounded-sm shadow-lg hover:bg-navy/90 text-sm font-medium max-w-[90vw]"
                data-testid="floating-cta-private">
                <Send size={14} className="inline mr-1" /> Invia il fascicolo al consulente
                <span className="hidden lg:block text-[11px] font-normal text-white/80 mt-0.5">Richiedi una valutazione professionale</span>
              </button>
            );
          }
          return (
            <button
              onClick={() => nav(`/case/${id}/premium`)}
              className="fixed bottom-4 right-4 lg:bottom-6 lg:right-6 z-40 bg-forensic-green text-white px-5 py-3 rounded-sm shadow-lg hover:bg-forensic-green/90 text-sm font-medium max-w-[90vw]"
              data-testid={`floating-cta-${ut}`}>
              <FileDown size={14} className="inline mr-1" /> Ottieni il Report Premium
              <span className="hidden lg:block text-[11px] font-normal text-white/80 mt-0.5">Con la tua intestazione professionale</span>
            </button>
          );
        })()}
      </main>

      {/* MODAL: Free send */}
      {showSend && (
        <div className="fixed inset-0 z-50 bg-ink/40 flex items-center justify-center p-4" onClick={() => setShowSend(false)} data-testid="send-modal">
          <div className="bg-white border hairline max-w-lg w-full p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-start">
              <div className="overline">Invio gratuito al consulente</div>
              <button onClick={() => setShowSend(false)} aria-label="Chiudi"><X size={16} /></button>
            </div>
            <h3 className="font-serif text-2xl font-semibold mt-2">Richiedi parere preliminare</h3>
            <p className="text-xs text-slate-600 mt-2">Il consulente riceverà il tuo fascicolo e ti chiamerà per un primo confronto orale.</p>
            <div className="space-y-3 mt-4">
              <input placeholder="Nome e cognome *" value={sendForm.name} onChange={(e) => setSendForm({...sendForm, name: e.target.value})} className="w-full border hairline px-3 py-2 rounded-sm bg-white" data-testid="send-name" />
              <input placeholder="Email *" type="email" value={sendForm.email} onChange={(e) => setSendForm({...sendForm, email: e.target.value})} className="w-full border hairline px-3 py-2 rounded-sm bg-white" data-testid="send-email" />
              <input placeholder="Telefono (facoltativo)" value={sendForm.phone} onChange={(e) => setSendForm({...sendForm, phone: e.target.value})} className="w-full border hairline px-3 py-2 rounded-sm bg-white" data-testid="send-phone" />
              <select value={sendForm.role} onChange={(e) => setSendForm({...sendForm, role: e.target.value})} className="w-full border hairline px-3 py-2 rounded-sm bg-white" data-testid="send-role">
                <option value="privato">Privato</option><option value="avvocato">Avvocato</option>
              </select>
              <textarea placeholder="Messaggio (facoltativo)" rows={3} value={sendForm.message} onChange={(e) => setSendForm({...sendForm, message: e.target.value})} className="w-full border hairline px-3 py-2 rounded-sm bg-white" data-testid="send-msg" />

              <label className="flex gap-2 items-start cursor-pointer border hairline p-3 rounded-sm bg-slate-50" data-testid="send-privacy-consent">
                <input type="checkbox" checked={sendForm.privacy_read} onChange={(e) => setSendForm({...sendForm, privacy_read: e.target.checked})} className="mt-1 accent-navy shrink-0" />
                <span className="text-xs text-slate-700 leading-snug">
                  Dichiaro di aver letto l'<a href="https://www.periziafirma.it/privacy-policy/" target="_blank" rel="noreferrer" className="text-navy underline">informativa privacy</a> e presto il consenso al trattamento dei dati personali per essere ricontattato dal consulente.
                </span>
              </label>

              {(caseData?.clinical?.documents?.length || caseData?.clinical?.conditions?.length || caseData?.clinical?.ricoveri?.length) ? (
                <label className="flex gap-2 items-start cursor-pointer border-2 border-forensic-amber p-3 rounded-sm bg-forensic-amber/5" data-testid="send-art9-consent">
                  <input type="checkbox" checked={sendForm.art9_consent} onChange={(e) => setSendForm({...sendForm, art9_consent: e.target.checked})} className="mt-1 accent-navy shrink-0" />
                  <span className="text-xs text-slate-700 leading-snug">
                    Consenso <b>ex art. 9 GDPR</b>: autorizzo il trattamento dei <b>dati particolari</b> (salute, ricoveri, condizioni cliniche) inseriti nel fascicolo, limitatamente al servizio.
                  </span>
                </label>
              ) : null}

              <button onClick={submitFreeSend} disabled={sending || !sendForm.privacy_read} className="w-full bg-forensic-green hover:brightness-110 text-white px-5 py-3 rounded-sm text-sm font-medium disabled:opacity-40 disabled:cursor-not-allowed" data-testid="send-submit">
                {sending ? <Loader2 className="animate-spin inline" size={14} /> : "Invia richiesta"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL: Checkout €50 */}
      {showCheckout && (
        <div className="fixed inset-0 z-50 bg-ink/40 flex items-center justify-center p-4 overflow-y-auto" onClick={() => setShowCheckout(false)} data-testid="checkout-modal">
          <div className="bg-white border hairline max-w-2xl w-full p-6 my-8" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-start">
              <div className="overline">Fascicolo completo · € 29,90</div>
              <button onClick={() => setShowCheckout(false)} aria-label="Chiudi"><X size={16} /></button>
            </div>
            <h3 className="font-serif text-2xl font-semibold mt-2">Dati per fatturazione</h3>
            <p className="text-xs text-slate-600 mt-2">
              Fattura in regime forfettario, senza IVA. Il credito di € 29,90 verrà detratto in caso di successivo conferimento incarico.
              <br />I campi contrassegnati con <strong>*</strong> sono obbligatori; PEC / SDI / Telefono sono facoltativi.
            </p>
            <div className="grid md:grid-cols-2 gap-3 mt-4">
              <input placeholder="Nome *" value={checkoutForm.first_name} onChange={(e) => setCheckoutForm({...checkoutForm, first_name: e.target.value})} className="border hairline px-3 py-2 rounded-sm bg-white" data-testid="co-first" />
              <input placeholder="Cognome *" value={checkoutForm.last_name} onChange={(e) => setCheckoutForm({...checkoutForm, last_name: e.target.value})} className="border hairline px-3 py-2 rounded-sm bg-white" data-testid="co-last" />
              <input placeholder="Codice Fiscale *" value={checkoutForm.fiscal_code} onChange={(e) => setCheckoutForm({...checkoutForm, fiscal_code: e.target.value.toUpperCase()})} className="border hairline px-3 py-2 rounded-sm bg-white" data-testid="co-cf" />
              <input placeholder="Partita IVA (in alternativa al CF)" value={checkoutForm.vat_number} onChange={(e) => setCheckoutForm({...checkoutForm, vat_number: e.target.value})} className="border hairline px-3 py-2 rounded-sm bg-white" data-testid="co-piva" />
              <input placeholder="Indirizzo *" value={checkoutForm.address_street} onChange={(e) => setCheckoutForm({...checkoutForm, address_street: e.target.value})} className="border hairline px-3 py-2 rounded-sm bg-white md:col-span-2" data-testid="co-street" />
              <input placeholder="CAP *" value={checkoutForm.address_zip} onChange={(e) => setCheckoutForm({...checkoutForm, address_zip: e.target.value})} className="border hairline px-3 py-2 rounded-sm bg-white" data-testid="co-zip" />
              <input placeholder="Città *" value={checkoutForm.address_city} onChange={(e) => setCheckoutForm({...checkoutForm, address_city: e.target.value})} className="border hairline px-3 py-2 rounded-sm bg-white" data-testid="co-city" />
              <input placeholder="Provincia (sigla) *" value={checkoutForm.address_province} onChange={(e) => setCheckoutForm({...checkoutForm, address_province: e.target.value.toUpperCase().slice(0, 2)})} className="border hairline px-3 py-2 rounded-sm bg-white" data-testid="co-prov" />
              <input placeholder="Email *" type="email" value={checkoutForm.email} onChange={(e) => setCheckoutForm({...checkoutForm, email: e.target.value})} className="border hairline px-3 py-2 rounded-sm bg-white" data-testid="co-email" />
              <input placeholder="PEC (facoltativa)" value={checkoutForm.pec} onChange={(e) => setCheckoutForm({...checkoutForm, pec: e.target.value})} className="border hairline px-3 py-2 rounded-sm bg-white md:col-span-2" data-testid="co-pec" />
              <input placeholder="Codice Destinatario SDI (facoltativo)" value={checkoutForm.sdi_code} onChange={(e) => setCheckoutForm({...checkoutForm, sdi_code: e.target.value.toUpperCase()})} className="border hairline px-3 py-2 rounded-sm bg-white" data-testid="co-sdi" />
              <input placeholder="Telefono (facoltativo)" value={checkoutForm.phone} onChange={(e) => setCheckoutForm({...checkoutForm, phone: e.target.value})} className="border hairline px-3 py-2 rounded-sm bg-white" data-testid="co-phone" />
            </div>
            <button onClick={submitCheckout} disabled={checkoutLoading} className="w-full bg-navy hover:bg-navy-dark text-white px-5 py-3 rounded-sm text-sm font-medium mt-5 disabled:opacity-60" data-testid="co-submit">
              {checkoutLoading ? <Loader2 className="animate-spin inline" size={14} /> : "Procedi al pagamento su Stripe · € 29,90"}
            </button>
          </div>
        </div>
      )}
      <Footer />
    </div>
  );
}
